<script setup>
// Chamadas chegando (com Atender/Recusar) e a chamada ativa (cronômetro, mudo,
// desligar). Fica no Dashboard, então sobrevive à troca de tela.
import { computed, ref, watch, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useWhatsappCallsStore } from 'dashboard/stores/whatsappCalls';
import {
  answerCall,
  rejectCall,
  hangup,
  toggleMute,
} from 'dashboard/helper/whatsappCall/session';
import {
  startRingtone,
  stopRingtone,
} from 'dashboard/helper/whatsappCall/ringtone';

const { t } = useI18n();
const router = useRouter();
const store = useWhatsappCallsStore();

const incoming = computed(() => store.incoming);
const active = computed(() => store.active);

// Toque em loop enquanto houver ligação chegando e nenhuma em curso. Todo caminho
// de saída (atender, recusar, perdida, cliente desligou) passa por removeIncoming
// ou por active, então o watch para o som sem código em cada ponto.
watch(
  () => store.ringing,
  ringing => (ringing ? startRingtone() : stopRingtone()),
  { immediate: true }
);
onUnmounted(stopRingtone);
const elapsed = ref(0);
let timer = null;

watch(
  () => active.value?.startedAt,
  startedAt => {
    clearInterval(timer);
    elapsed.value = 0;
    if (!startedAt) return;
    timer = setInterval(() => {
      elapsed.value = Math.floor((Date.now() - startedAt) / 1000);
    }, 1000);
  },
  { immediate: true }
);
onUnmounted(() => clearInterval(timer));

const clock = computed(() => {
  const m = String(Math.floor(elapsed.value / 60)).padStart(2, '0');
  const s = String(elapsed.value % 60).padStart(2, '0');
  return `${m}:${s}`;
});

const statusText = computed(() =>
  active.value?.status === 'ringing'
    ? t('CONVERSATION.WHATSAPP_CALL.CALLING')
    : clock.value
);

const answering = ref(null);

const answer = async call => {
  if (answering.value) return;
  answering.value = call.callId;
  try {
    await answerCall(call);
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: call.conversationId },
    });
  } catch (error) {
    useAlert(
      error?.response?.status === 409
        ? t('CONVERSATION.WHATSAPP_CALL.TAKEN')
        : t('CONVERSATION.WHATSAPP_CALL.MIC_ERROR')
    );
  } finally {
    answering.value = null;
  }
};

const reject = async call => {
  try {
    await rejectCall(call);
  } catch {
    useAlert(t('CONVERSATION.WHATSAPP_CALL.REJECT_ERROR'));
  }
};
</script>

<template>
  <div
    class="fixed ltr:right-4 rtl:left-4 bottom-4 z-50 flex flex-col gap-2 w-80"
  >
    <div
      v-for="call in incoming"
      :key="call.callId"
      class="flex items-center gap-3 p-4 bg-n-solid-2 rounded-xl shadow-xl outline outline-1 outline-n-strong"
    >
      <div
        class="flex justify-center items-center rounded-full size-10 bg-n-teal-9 animate-pulse shrink-0"
      >
        <i class="text-lg text-white i-ph-phone-incoming-bold" />
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium text-n-slate-12 truncate mb-0">
          {{ call.contactName }}
        </p>
        <p class="text-xs text-n-slate-11 truncate">
          {{ $t('CONVERSATION.WHATSAPP_CALL.INCOMING') }}
        </p>
      </div>
      <div class="flex shrink-0 gap-2">
        <button
          v-tooltip="$t('CONVERSATION.WHATSAPP_CALL.REJECT')"
          class="flex justify-center items-center w-10 h-10 bg-n-ruby-9 hover:bg-n-ruby-10 rounded-full transition-colors disabled:opacity-50"
          :disabled="!!answering"
          @click="reject(call)"
        >
          <i class="text-lg text-white i-ph-phone-x-bold" />
        </button>
        <button
          v-tooltip="$t('CONVERSATION.WHATSAPP_CALL.ANSWER')"
          class="flex justify-center items-center w-10 h-10 bg-n-teal-9 hover:bg-n-teal-10 rounded-full transition-colors disabled:opacity-50"
          :disabled="!!answering || !!active"
          @click="answer(call)"
        >
          <i class="text-lg text-white i-ph-phone-bold" />
        </button>
      </div>
    </div>

    <div
      v-if="active"
      class="flex items-center gap-3 p-4 bg-n-solid-2 rounded-xl shadow-xl outline outline-1 outline-n-strong"
    >
      <div
        class="flex justify-center items-center rounded-full size-10 bg-n-teal-9 shrink-0"
      >
        <i class="text-lg text-white i-ph-phone-call-bold" />
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium text-n-slate-12 truncate mb-0">
          {{ active.contactName }}
        </p>
        <p class="text-xs text-n-slate-11 tabular-nums">{{ statusText }}</p>
      </div>
      <div class="flex shrink-0 gap-2">
        <button
          v-tooltip="
            active.muted
              ? $t('CONVERSATION.WHATSAPP_CALL.UNMUTE')
              : $t('CONVERSATION.WHATSAPP_CALL.MUTE')
          "
          class="flex justify-center items-center w-10 h-10 rounded-full transition-colors"
          :class="
            active.muted
              ? 'bg-n-amber-9 hover:bg-n-amber-10'
              : 'bg-n-slate-6 hover:bg-n-slate-7'
          "
          @click="toggleMute"
        >
          <i
            class="text-lg"
            :class="
              active.muted
                ? 'text-white i-ph-microphone-slash-bold'
                : 'text-n-slate-12 i-ph-microphone-bold'
            "
          />
        </button>
        <button
          v-tooltip="$t('CONVERSATION.WHATSAPP_CALL.HANGUP')"
          class="flex justify-center items-center w-10 h-10 bg-n-ruby-9 hover:bg-n-ruby-10 rounded-full transition-colors"
          @click="hangup"
        >
          <i class="text-lg text-white i-ph-phone-disconnect-bold" />
        </button>
      </div>
    </div>
  </div>
</template>
